import React, { createContext, useContext, useEffect, useState } from 'react';
import { loginWithToken } from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'checkin_token';

/**
 * Provides Annual Check-In client authentication state throughout the app.
 *
 * Tokens are stored in localStorage under the key `checkin_token`.
 */
export function AuthProvider({ children }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setLoading(false);
      return;
    }
    try {
      const [, payload] = stored.split('.');
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const decoded = JSON.parse(atob(padded));
      if (decoded.exp && Date.now() / 1000 > decoded.exp) {
        localStorage.removeItem(TOKEN_KEY);
        setLoading(false);
        return;
      }
      setClient({ client_id: decoded.sub, name: decoded.name || '' });
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    }
    setLoading(false);
  }, []);

  /**
   * Log in using a raw magic-link token.
   * @param {string} rawToken
   */
  async function login(rawToken) {
    setError(null);
    try {
      const data = await loginWithToken(rawToken);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setClient({ client_id: data.client_id, name: data.name });
      return data;
    } catch (err) {
      // Surface the real failure mode so we don't always blame "expired link".
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg;
      if (detail) {
        msg = detail;
      } else if (status) {
        msg = `Login failed (HTTP ${status}). Please contact your advisor.`;
      } else {
        // No response at all → network/CORS/mixed-content failure.
        msg = `Could not reach the login service (${err.message || 'network error'}). Please contact your advisor.`;
      }
      setError(msg);
      throw new Error(msg);
    }
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setClient(null);
  }

  return (
    <AuthContext.Provider value={{ client, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
